import {
  StepCaptureData,
  StepDetailsData,
} from "contexts/MultiStepCaptureContext";
import {
  createOrbConfirmationFingerprint,
  getApplicableOrbDetailValues,
  getApplicableStoredOrbDetails,
  getQrRetryDetail,
} from "lib/orb-qr";
import { getStepRouteState, getStepSchemaFingerprint } from "lib/task-steps";
import {
  getOrbStepDetailFields,
  ResolvedWorkTask,
  ResolvedWorkTaskStep,
} from "lib/work-tasks";
import { AppRoutes, isQrCaptureType } from "types";
import {
  getRtmsRun,
  getRtmsSectionStart,
  getRtmsStepFingerprint,
} from "lib/rtms-sequence";

export function getWorkTaskStepRoute(step: ResolvedWorkTaskStep): AppRoutes {
  if (step.rtmsSequence) return AppRoutes.rtmsSequence;
  if (step.captureType === "rtms") return AppRoutes.stepRtms;
  return step.captureType === "orb" ? AppRoutes.stepOrb : AppRoutes.stepCapture;
}

export function getWorkTaskStepRouteState(
  workTask: ResolvedWorkTask,
  step: ResolvedWorkTaskStep,
  editMode = false,
  navigationEntryId?: string
) {
  const routeStep = getRtmsSectionStart(workTask, step);
  return {
    ...getStepRouteState(routeStep, workTask.steps.length, {
      taskId: workTask.workTaskId,
      taskName: workTask.workTaskName,
      captureType: step.captureType,
      editMode,
    }),
    ...(navigationEntryId ? { navigationEntryId } : {}),
  };
}

export function getWorkTaskReviewDestination(
  workTask: ResolvedWorkTask,
  isStepComplete: (stepNumber: number) => boolean
) {
  const firstIncompleteStep = workTask.steps.find(
    (step) => !isStepComplete(step.stepNumber)
  );

  if (firstIncompleteStep) {
    return {
      route: getWorkTaskStepRoute(firstIncompleteStep),
      state: getWorkTaskStepRouteState(workTask, firstIncompleteStep, true),
    };
  }

  return {
    route: AppRoutes.multiStepUpload,
    state: {
      taskId: workTask.workTaskId,
      taskName: workTask.workTaskName,
    },
  };
}

export function getStoredWorkTaskSharedDetails(
  workTask: ResolvedWorkTask,
  captures: StepCaptureData[]
) {
  const storedDetails = captures.find(
    (capture) => capture.stepNumber === 0
  )?.collectionDetails;
  return getApplicableStoredOrbDetails(
    storedDetails,
    workTask.sharedCollectionDetailFields.map(
      (field) => field.label_id?.toString().trim() || ""
    )
  );
}

export function isStoredCaptureForWorkTaskStep(
  step: ResolvedWorkTaskStep,
  capture: StepCaptureData
): boolean {
  return (
    capture.stepNumber === step.stepNumber &&
    capture.captureTaskId === step.captureTaskId &&
    (!capture.captureEntryId ||
      capture.captureEntryId === step.captureEntryId) &&
    capture.captureTaskStepNumber === step.captureTaskStepNumber &&
    capture.captureType === step.captureType &&
    capture.numberOfCaptures === step.numberOfCaptures
  );
}

export function hasChangedTaskPositions(
  flow: ResolvedWorkTask,
  records: Array<Pick<StepCaptureData, "stepNumber" | "captureEntryId">>
): boolean {
  return records.some((record) =>
    Boolean(
      record.captureEntryId &&
        flow.steps.find((step) => step.stepNumber === record.stepNumber)
          ?.captureEntryId !== record.captureEntryId
    )
  );
}

export function getWorkTaskStepSchemaFingerprint(
  step: ResolvedWorkTaskStep
): string {
  const seen = new Set<string>();
  const fields = [
    ...(isQrCaptureType(step.captureType) || step.isFirstCaptureTaskStep
      ? step.collectionDetailFields
      : []),
    ...step.stepDetailFields,
  ].filter((field) => {
    const id = field.label_id?.toString().trim() || "";
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  const schema = getStepSchemaFingerprint({
    captureType: step.captureType,
    numberOfCaptures: step.numberOfCaptures,
    fields,
    stepLabel:
      isQrCaptureType(step.captureType) || step.configuredStepLabel
        ? step.stepLabel
        : undefined,
  });
  return step.rtmsSequence
    ? JSON.stringify({ schema, rtmsPlan: step.rtmsPlanFingerprint })
    : schema;
}

export function isWorkTaskStepComplete(
  step: ResolvedWorkTaskStep,
  captures: StepCaptureData[],
  details: StepDetailsData[],
  sharedDetails: unknown
): boolean {
  const capture = captures.find((candidate) =>
    isStoredCaptureForWorkTaskStep(step, candidate)
  );
  if (!capture) return false;
  if (capture.schemaFingerprint !== getWorkTaskStepSchemaFingerprint(step)) {
    return false;
  }

  if (!isQrCaptureType(step.captureType)) {
    return Boolean(
      capture.files.length >= step.numberOfCaptures &&
        (capture.isComplete ?? true)
    );
  }

  if (step.rtmsSequence) {
    if (
      !capture.rtmsConfirmation ||
      !getRtmsRun(details)?.id ||
      capture.rtmsConfirmation.runId !== getRtmsRun(details)?.id
    )
      return false;
    try {
      return (
        capture.rtmsConfirmation.fingerprint ===
        getRtmsStepFingerprint(
          step,
          details,
          sharedDetails,
          capture.rtmsConfirmation.attempt
        )
      );
    } catch {
      return false;
    }
  }

  if (!capture.orbConfirmation || step.orbExtension === null) return false;
  const savedFields = details.find(
    (candidate) => candidate.stepNumber === step.stepNumber
  )?.stepDetailsFields;
  const applicableDetails = getApplicableOrbDetailValues(
    savedFields,
    getOrbStepDetailFields(step).map(
      (field) => field.label_id?.toString().trim() || ""
    )
  );

  return (
    capture.orbConfirmation.fingerprint ===
    createOrbConfirmationFingerprint({
      captureTaskId: step.captureTaskId,
      stepLabel: step.stepLabel,
      extension: step.orbExtension,
      storedDetails: sharedDetails,
      stepDetails: [
        ...applicableDetails,
        ...getQrRetryDetail(capture.orbRetryCount ?? 0, step.captureType),
      ],
    })
  );
}
